import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, onSnapshot, query } from "firebase/firestore";
import { db } from "../firebase";

// Helper function to get color classes
const getColorClasses = (color: string) => {
  const colorMap: Record<
    string,
    { border: string; bg: string; bgLight: string; text: string; hover: string }
  > = {
    blue: {
      border: "border-blue-500",
      bg: "bg-blue-500",
      bgLight: "bg-blue-100",
      text: "text-blue-600",
      hover: "hover:bg-blue-600",
    },
    green: {
      border: "border-green-500",
      bg: "bg-green-500",
      bgLight: "bg-green-100",
      text: "text-green-600",
      hover: "hover:bg-green-600",
    },
    purple: {
      border: "border-purple-500",
      bg: "bg-purple-500",
      bgLight: "bg-purple-100",
      text: "text-purple-600",
      hover: "hover:bg-purple-600",
    },
    red: {
      border: "border-red-500",
      bg: "bg-red-500",
      bgLight: "bg-red-100",
      text: "text-red-600",
      hover: "hover:bg-red-600",
    },
  };

  return colorMap[color] || colorMap.blue; // Default to blue if color not found
};

type FirestoreBatch = {
  id: string;
  name: string;
  class: "IX" | "X" | "XI" | "XII" | string;
  location: string;
  schedule: string;
  startDate?: string;
  maxStudents?: number;
  curriculum?: string;
  createdAt?: any;
};

const levelForClass = (cls: string) =>
  cls === "IX" || cls === "X" ? "O Level" : "A Level";

const colorForClass: Record<string, "blue" | "green" | "purple" | "red"> = {
  IX: "blue",
  X: "green",
  XI: "purple",
  XII: "red",
};

const Batches: React.FC = () => {
  const [all, setAll] = useState<FirestoreBatch[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "batches"));
    const unsub = onSnapshot(q, (snap) => {
      const data: FirestoreBatch[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<FirestoreBatch, "id">),
      }));
      setAll(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const classes = ["IX", "X", "XI", "XII"] as const;

  // Pick the most recent batch per class (by createdAt if present)
  const selected = useMemo(() => {
    const pick: Record<string, FirestoreBatch | undefined> = {};
    for (const cls of classes) {
      const items = all.filter((b) => b.class === cls);
      if (items.length === 0) {
        pick[cls] = undefined;
        continue;
      }
      items.sort((a, b) => {
        const ad = (a as any).createdAt?.seconds || 0;
        const bd = (b as any).createdAt?.seconds || 0;
        return bd - ad; // desc
      });
      pick[cls] = items[0];
    }
    return pick;
  }, [all]);

  // Build cards with fallbacks when data missing
  const cards = classes.map((cls, idx) => {
    const b = selected[cls];
    const color = colorForClass[cls];
    return {
      id: idx + 1,
      name: b?.name || `Class ${cls}`,
      level: levelForClass(cls),
      schedule: b?.schedule || "Schedule will be announced",
      location: b?.location || "Location will be announced",
      color,
    };
  });

  return (
    <section id="batches" className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Batches & Schedule
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-500">
            Join our specialized chemistry batches tailored to your educational needs
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          {cards.map((batch) => (
            <div
              key={batch.id}
              className={`bg-white rounded-lg shadow-md overflow-hidden border-t-4 ${
                getColorClasses(batch.color).border
              } hover:shadow-lg transition duration-300`}
            >
              <div className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {batch.name}
                    </h3>
                    <p
                      className={`inline-block px-2 py-1 rounded-full text-xs font-semibold text-gray-800 ${
                        getColorClasses(batch.color).bgLight
                      } mt-2`}
                    >
                      {batch.level}
                    </p>
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  <div className="flex items-start">
                    <svg
                      className="h-5 w-5 text-gray-400 mt-0.5 mr-2"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-gray-600">{batch.schedule}</span>
                  </div>

                  <div className="flex items-start">
                    <svg
                      className="h-5 w-5 text-gray-400 mt-0.5 mr-2"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M10 20s6-5.686 6-10a6 6 0 10-12 0c0 4.314 6 10 6 10zm0-8a2 2 0 110-4 2 2 0 010 4z" />
                    </svg>
                    <span className="text-gray-600">{batch.location}</span>
                  </div>
                </div>

                <div className="mt-6">
                  <button
                    className={`w-full ${getColorClasses(batch.color).bg} ${
                      getColorClasses(batch.color).hover
                    } text-white py-2 px-4 rounded-md font-medium transition duration-300`}
                  >
                    Enroll Now
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-10 flex justify-center">
          <Link
            to="/batches"
            className="inline-flex items-center px-6 py-3 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50 font-medium"
          >
            View All
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Batches;
